import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { FaEye, FaEyeSlash } from "react-icons/fa";

const API_URL = "https://awriter-backend-si7g.onrender.com";

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    console.log("ResetPassword route loaded with token:", token);
    if (!token) {
      setError("No reset token provided");
      toast.error("Invalid reset link");
      return;
    }

    const validateToken = async () => {
      try {
        await axios.get(`${API_URL}/api/admin/validate-reset-token/${token}`);
        console.log("Reset token is valid");
      } catch (err) {
        console.error("Token validation error:", err.response?.data || err.message);
        setError("Invalid or expired reset token");
        toast.error("Invalid or expired reset link");
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      }
    };
    validateToken();
  }, [token, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccessMessage("");

    if (formData.newPassword !== formData.confirmPassword) {
      setError("Passwords do not match");
      toast.error("Passwords do not match");
      setLoading(false);
      return;
    }

    if (formData.newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      toast.error("Password must be at least 8 characters");
      setLoading(false);
      return;
    }

    try {
      console.log("Sending reset password request:", { token: "[HIDDEN]" });
      const response = await axios.post(`${API_URL}/api/admin/reset-password`, {
        token,
        newPassword: formData.newPassword,
      });
      console.log("Reset password response:", response.data);
      setSuccessMessage("Password reset successfully! Redirecting to login...");
      toast.success("Password reset successfully!");
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (err) {
      console.error("Reset password error:", err.response?.data || err.message);
      const errorMessage = err.response?.data?.error || "Failed to reset password";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full flex flex-col justify-center items-center min-h-screen p-4">
      <form
        onSubmit={handleSubmit}
        className="flex flex-col w-[90%] md:w-[50%] space-y-5 p-4"
      >
        <h1 className="text-4xl md:text-6xl mb-10 text-center">
          Reset Your Password
        </h1>
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            name="newPassword"
            className="border-b-2 pb-2 outline-none w-full"
            placeholder="New Password"
            value={formData.newPassword}
            onChange={handleChange}
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600"
          >
            {showPassword ? <FaEyeSlash /> : <FaEye />}
          </button>
        </div>
        <input
          type={showPassword ? "text" : "password"}
          name="confirmPassword"
          className="border-b-2 pb-2 outline-none w-full"
          placeholder="Confirm Password"
          value={formData.confirmPassword}
          onChange={handleChange}
          required
        />
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}
        {successMessage && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-600 text-sm">{successMessage}</p>
          </div>
        )}
        <div className="w-full md:w-[80%] mx-auto">
          <button
            type="submit"
            className="bg-[#D44D76] text-white mt-5 p-2 w-full disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading}
          >
            {loading ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Processing...
              </div>
            ) : (
              "Reset Password"
            )}
          </button>
        </div>
        <div className="text-center mt-4">
          <button
            type="button"
            onClick={() => navigate("/login")}
            className="text-sm text-gray-600 hover:underline"
          >
            ← Back to login
          </button>
        </div>
      </form>
    </div>
  );
};

export default ResetPassword;